import { useCallback, useMemo, useRef, useState } from 'react'
import { createFileRoute, useRouter } from '@tanstack/react-router'
import { FullView, FullViewContent } from '../../../components/Modal'
import { ActionButton } from '../../../components/Button'
import { Check, ChevronBackward } from '@nine-thirty-five/material-symbols-react/rounded'
import { type HabitFormData, useCreateHabit, useGetHabit } from '@zendia/frontend-shared'
import { axiosRequest } from '../../../utilities/axiosClient'
import { HabitForm, type HabitFormRef } from '../../../components/Habits/HabitForm'

export const Route = createFileRoute('/_authenticated/habits/new')({
  component: RouteComponent,
  staticData: { suppressNotifications: true },
  validateSearch: (
    search,
  ): {
    parentId?: string
    goalId?: string
    milestoneId?: string
  } => ({
    parentId: search.parentId as string | undefined,
    // Set when adding a habit from a goal or milestone: pre-selects it in the Goal picker, and
    // saving (or discarding) returns to its Your Plan, on that milestone.
    goalId: search.goalId as string | undefined,
    milestoneId: search.milestoneId as string | undefined,
  }),
})

function RouteComponent() {
  const router = useRouter()
  const { parentId, goalId, milestoneId } = Route.useSearch()
  const { mutateAsync: createHabit } = useCreateHabit(axiosRequest)
  const { data: parentResponse } = useGetHabit(axiosRequest, parentId ?? '', !!parentId)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string>()
  const habitFormRef = useRef<HabitFormRef>(null)
  // Set by "Add Another" so the save that follows clears the form instead of leaving it
  const keepAddingRef = useRef(false)

  const routineName = parentResponse?.data?.name

  const leave = useCallback(() => {
    if (goalId) {
      router.navigate({
        to: '/goals/$goalId',
        params: { goalId },
        search: { tab: 'plan', planMilestone: milestoneId },
        replace: true,
      })
    } else {
      router.navigate({ to: '/dashboard', replace: true })
    }
  }, [router, goalId, milestoneId])

  const onBack = useCallback(() => {
    if (isSubmitting) return
    leave()
  }, [isSubmitting, leave])

  const initialGoal = useMemo(
    () => (goalId ? { goalRefinementId: goalId, milestoneId } : undefined),
    [goalId, milestoneId],
  )

  const onSave = useCallback(
    async (data: HabitFormData) => {
      const keepAdding = keepAddingRef.current
      keepAddingRef.current = false

      setIsSubmitting(true)
      setSubmitError(undefined)

      try {
        const response = await createHabit({
          ...data,
          parentId: data.parentId || parentId,
          lifeDomainId: data.lifeDomainId ?? undefined,
        })

        if (keepAdding) {
          habitFormRef.current?.reset()
          return
        }

        // A routine on its own does nothing until it has habits in it, so hand the user
        // straight to the form for its first one rather than back to the dashboard.
        const newRoutineId = data.isRoutine ? response?.data?.habitId : undefined
        if (newRoutineId) {
          router.navigate({
            to: '/habits/new',
            // Its habits inherit the routine's link; the rest only carries the way back
            search: {
              parentId: newRoutineId,
              goalId,
              milestoneId,
            },
            replace: true,
          })
          return
        }

        leave()
      } catch (error) {
        console.error(error)
        setSubmitError('Failed to create habit')
      } finally {
        setIsSubmitting(false)
      }
    },
    [createHabit, router, parentId, leave, goalId, milestoneId],
  )

  const onAddAnother = useCallback(() => {
    keepAddingRef.current = true
    habitFormRef.current?.submit()
  }, [])

  const title = parentId ? `Add Habit to ${routineName ?? 'Routine'}` : 'New Habit/Routine'

  return (
    <FullView
      leftAction={
        <ActionButton
          onClick={onBack}
          label="Discard"
          icon={<ChevronBackward className="size-6" />}
        />
      }
      centerContent={<div>{title}</div>}
      rightAction={
        <ActionButton
          primary
          onClick={() => {
            habitFormRef.current?.submit()
          }}
          label="Save"
          disabled={isSubmitting}
          icon={<Check className="m-auto size-6" />}
        />
      }
    >
      <FullViewContent>
        <div className="h-full">
          {/* Keyed on the routine so the redirect after creating one lands on a
              blank form rather than the routine's own values */}
          <HabitForm
            key={parentId ?? 'standalone'}
            ref={habitFormRef}
            parentId={parentId}
            initialGoal={initialGoal}
            onSubmit={onSave}
            isSubmitting={isSubmitting}
            submitError={submitError}
          />

          {parentId && (
            <button
              type="button"
              onClick={onAddAnother}
              disabled={isSubmitting}
              className="w-full px-4 py-2 rounded-xl border border-gray-300 dark:border-gray-600 text-sm font-medium hover:bg-gray-50 dark:hover:bg-white/5 disabled:opacity-50"
            >
              Add Another
            </button>
          )}
        </div>
      </FullViewContent>
    </FullView>
  )
}
